import * as i0 from '@angular/core';
import { InjectionToken, NgModule, Optional, SkipSelf } from '@angular/core';
import { createServiceApi, serviceTypes, combineEffectsWithProviders } from '@essent/common';
import * as i1 from '@ngrx/effects';
import { EffectsModule } from '@ngrx/effects';
import { camelCase } from '@essent/core-utils';

/**
 * @ignore
 */
const API = new InjectionToken('api-billing');

const apiVersion$4 = '1';
const calculatePenaltyFeeApi = createServiceApi('[BillingState] Calculate penalty fee', 'post', serviceTypes(), {
    getUrl: `/billing/penaltyfee/v${apiVersion$4}`,
    version: apiVersion$4,
    defaultHeaders: { Accept: 'application/json' },
    getParams: ({ accountId }) => ({ accountId }),
    getBody: ({ body }) => camelCase(body)
});

const apiVersion$3 = '1';
const getPricesApi = createServiceApi('[BillingState] Get prices', 'get', serviceTypes(), {
    getUrl: `/billing/prices/v${apiVersion$3}`,
    version: apiVersion$3,
    defaultHeaders: {
        Accept: 'application/json'
    },
    getParams: payload => payload
});

const apiVersion$2 = '1';
const getYearlyInvoiceV1Api = createServiceApi('[BillingState] Get yearly invoice v1', 'get', serviceTypes(), {
    getUrl: `/billing/invoices/yearly-invoice/v${apiVersion$2}`,
    version: apiVersion$2,
    defaultHeaders: {
        Accept: 'application/json'
    },
    getParams: payload => payload
});

const apiVersion$1 = '2';
const getYearlyInvoiceV2Api = createServiceApi('[BillingState] Get yearly invoice v2', 'get', serviceTypes(), {
    getUrl: `/billing/invoices/yearly-invoice/v${apiVersion$1}`,
    version: apiVersion$1,
    defaultHeaders: {
        Accept: 'application/json'
    },
    getParams: payload => payload
});

const apiVersion = '1';
const getInvoiceV1Api = createServiceApi('[BillingState] Get invoice v1', 'get', serviceTypes(), {
    getUrl: `/billing/invoices/v${apiVersion}`,
    version: apiVersion,
    defaultHeaders: {
        Accept: 'application/json'
    },
    getParams: payload => payload
});

const { EFFECTS, PROVIDERS } = combineEffectsWithProviders(getPricesApi, calculatePenaltyFeeApi, getYearlyInvoiceV1Api, getYearlyInvoiceV2Api, getInvoiceV1Api);
class BillingCoreModule {
    constructor(parentModule) {
        if (parentModule) {
            throw new Error('BillingCoreModule is imported more than once.');
        }
    }
    static forRoot(config) {
        return {
            ngModule: BillingCoreModule,
            providers: [
                {
                    provide: API,
                    useValue: config ? config.api : undefined
                }
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: BillingCoreModule, deps: [{ token: BillingCoreModule, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: BillingCoreModule, imports: [i1.EffectsFeatureModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: BillingCoreModule, providers: PROVIDERS, imports: [EffectsModule.forFeature(EFFECTS)] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: BillingCoreModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [EffectsModule.forFeature(EFFECTS)],
                    providers: PROVIDERS
                }]
        }], ctorParameters: () => [{ type: BillingCoreModule, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }] });

const { getPricesActions, getPrices, getPricesSuccess, getPricesError, getPricesClear } = getPricesApi;

const { getYearlyInvoiceV1Actions, getYearlyInvoiceV1, getYearlyInvoiceV1Success, getYearlyInvoiceV1Error, getYearlyInvoiceV1Clear } = getYearlyInvoiceV1Api;

var PriceGroupType;
(function (PriceGroupType) {
    PriceGroupType["SupplyCost"] = "VER";
    PriceGroupType["GridCost"] = "NET";
    PriceGroupType["VatAndSurplus"] = "TAX";
    PriceGroupType["ReturnSupply"] = "RET";
    PriceGroupType["Discount"] = "DIS";
    PriceGroupType["PVScales"] = "PVS";
    PriceGroupType["FixedCosts"] = "FXD";
    PriceGroupType["ReasonableRate"] = "RRT";
})(PriceGroupType || (PriceGroupType = {}));

const { getYearlyInvoiceV2Actions, getYearlyInvoiceV2, getYearlyInvoiceV2Success, getYearlyInvoiceV2Error, getYearlyInvoiceV2Clear } = getYearlyInvoiceV2Api;

var ConsumptionType;
(function (ConsumptionType) {
    ConsumptionType["Consumption"] = "C";
    ConsumptionType["ReturnSupply"] = "R";
})(ConsumptionType || (ConsumptionType = {}));

const { calculatePenaltyFeeActions, calculatePenaltyFee, calculatePenaltyFeeSuccess, calculatePenaltyFeeError, calculatePenaltyFeeClear } = calculatePenaltyFeeApi;

var PenaltyReasonCode;
(function (PenaltyReasonCode) {
    PenaltyReasonCode["NOT_A_FIXED_CONTRACT"] = "001";
    PenaltyReasonCode["CONTRACT_IS_IN_COOLDOWN_PERIOD"] = "002";
    PenaltyReasonCode["PENALTY_LESS_THAN_ZERO"] = "003";
    PenaltyReasonCode["NO_PENALTY"] = "004";
    PenaltyReasonCode["PLANNED_END_DATE_AFTER_CONTRACT_END_DATE"] = "005";
    PenaltyReasonCode["GO_LIVE_NOT_REACHED"] = "006";
    PenaltyReasonCode["END_OF_SUPPLY_REGISTERED"] = "BE-001";
    PenaltyReasonCode["NO_PENALTY_REQUIRED_FOR_REASON"] = "BE-004";
})(PenaltyReasonCode || (PenaltyReasonCode = {}));
var ComponentType;
(function (ComponentType) {
    ComponentType["ZGGCEQ"] = "Gas";
    ComponentType["ZDGCEQ"] = "Gas";
    ComponentType["ZCGCEQ"] = "Gas";
    ComponentType["ZEECLQ"] = "Dal";
    ComponentType["ZAECLQ"] = "Dal";
    ComponentType["ZAECDQ"] = "Dal";
    ComponentType["ZBECDQ"] = "Dal";
    ComponentType["ZBECLQ"] = "Dal";
    ComponentType["ZEECNQ"] = "Normaal";
    ComponentType["ZAECNQ"] = "Normaal";
    ComponentType["ZAECPQ"] = "Normaal";
    ComponentType["ZBECNQ"] = "Normaal";
    ComponentType["ZBECPQ"] = "Normaal";
    ComponentType["ZAECEQ"] = "Normaal";
    ComponentType["ZBECEQ"] = "Normaal";
})(ComponentType || (ComponentType = {}));
var PropositionType;
(function (PropositionType) {
    PropositionType["REFERENCE_PROPOSTION"] = "R";
    PropositionType["SWITCH_OUT_PROPOSITION"] = "S";
})(PropositionType || (PropositionType = {}));

const { getInvoiceV1Actions, getInvoiceV1, getInvoiceV1Success, getInvoiceV1Error, getInvoiceV1Clear } = getInvoiceV1Api;

/**
 * Generated bundle index. Do not edit.
 */

export { API, BillingCoreModule, ComponentType, ConsumptionType, PenaltyReasonCode, PriceGroupType, PropositionType, calculatePenaltyFee, calculatePenaltyFeeActions, calculatePenaltyFeeClear, calculatePenaltyFeeError, calculatePenaltyFeeSuccess, getInvoiceV1, getInvoiceV1Actions, getInvoiceV1Api, getInvoiceV1Clear, getInvoiceV1Error, getInvoiceV1Success, getPrices, getPricesActions, getPricesClear, getPricesError, getPricesSuccess, getYearlyInvoiceV1, getYearlyInvoiceV1Actions, getYearlyInvoiceV1Clear, getYearlyInvoiceV1Error, getYearlyInvoiceV1Success, getYearlyInvoiceV2, getYearlyInvoiceV2Actions, getYearlyInvoiceV2Clear, getYearlyInvoiceV2Error, getYearlyInvoiceV2Success };
